export class Business {
  id = '';
  name = '';
  phoneNumber = '';
  email = '';
  address: Address = new Address();
  taxCode = '';
  employerIdNum = '';
  website = '';
  timezone = '';
  /** operating lifecycle, odin clients only (2026-09-03): may they trade. undefined for a client born before Juno */
  /** odin's one lifecycle: INTAKE | PENDING_REVIEW | APPROVED | PROVISIONED | LIVE | SUSPENDED | CLOSED */
  status?: 'INTAKE' | 'PENDING_REVIEW' | 'APPROVED' | 'PROVISIONED' | 'LIVE' | 'SUSPENDED' | 'CLOSED';
  statusChangedAt?: string | null;
  /** why and from where a CLOSED client was closed (odin 2026-09-04) */
  closedReason?: string | null;
  closedFromStatus?: string | null;
  /** FT's own account: badge it, keep it out of business metrics */
  internal?: boolean;
  /** SALON | FNB */
  vertical?: string;
  /** odin clients only (FT-219): the registered name, and the salon sub-type (NAIL_SALON | HAIR_SALON) */
  legalName?: string | null;
  businessType?: string;
  /** stamped when the point of sale first knew this client, and when it went live. odin clients only */
  provisionedAt?: string | null;
  liveAt?: string | null;
}

export class Store {
  id = '';
  businessId = ''
  name = '';
  openingDate = '';
  businessType: BusinessType = BusinessType.OTHER;
  phoneNumber = '';
  address: Address = new Address();
  businessHours: Map<DayOfWeek, BusinessHour> = new Map<DayOfWeek, BusinessHour>();
  website = '';
  testMode = false;
  active?: boolean;
  platformConfig?: PlatformConfig = new PlatformConfig();
  googleBookingConfig?: GoogleBookingConfig = new GoogleBookingConfig();
  timezone = '';
  zoneId = '';
}

export class PlatformConfig {
  enable = false;
  clientEditable = false;
}

export class GoogleBookingConfig {
  enable = false;
  clientEditable = false;
}

export class BusinessData {
  name = '';
  phoneNumber = '';
  email = '';
  address: Address = new Address();
  taxCode = '';
  employerIdNum = '';
  website = '';
  timezone = '';
}

export class StoreData {
  name = '';
  businessId = '';
  businessType: BusinessType = BusinessType.OTHER;
  openingDate = '';
  phoneNumber = '';
  businessHours: Map<string, BusinessHourByStr> = new Map<string, BusinessHourByStr>();
  platformConfig: PlatformConfig = new PlatformConfig();
  googleBookingConfig: GoogleBookingConfig = new GoogleBookingConfig();
  address: Address = new Address();
  website = '';
  timezone = '';
  zoneId = '';
  testMode = false;
}

export class BusinessUpdateReq {
  name?: string;
  phoneNumber?: string;
  email?: string;
  address?: Address;
  taxCode?: string;
  employerIdNum?: string;
  website?: string;
  timezone?: string;
  /** odin clients only (FT-219) */
  legalName?: string;
  businessType?: string;

  constructor(init?: Partial<BusinessUpdateReq>) {
    Object.assign(this, init);
  }
}



export class StoreUpdateReq {
  active?: boolean;
  testMode?: boolean;
  name?: string;
  businessType?: BusinessType;
  phoneNumber?: string;
  address?: Address;
  website?: string;
  businessHours?: Record<DayOfWeek, BusinessHour>;
  timezone?: string;
  zoneId?: string;
  openingDate?: string;
  platformConfig?: PlatformConfig;
  googleBookingConfig?: GoogleBookingConfig;

  constructor(init?: Partial<StoreUpdateReq>) {
    Object.assign(this, init);
  }

  isEmpty(): boolean {
    return Object.keys(this).length == 0
  }
}

export class BusinessHourByStr {
  open = true;
  openTime = '09:00';
  closeTime = '17:00';

  static fromBusinessHour(businessHour: BusinessHour): BusinessHourByStr {
    return {
      open: businessHour.open,
      openTime: businessHour.openTime.toString(),
      closeTime: businessHour.closeTime.toString()
    } as BusinessHourByStr
  }

  toBusinessHour(): BusinessHour {
    return {
      open: this.open,
      openTime: SimpleTime.fromString(this.openTime),
      closeTime: SimpleTime.fromString(this.closeTime)
    }
  }
}

export class BusinessHour {
  open = true;
  openTime: SimpleTime = new SimpleTime(9, 0);
  closeTime: SimpleTime = new SimpleTime(17, 0);
}

export class SimpleTime {
  hour: number
  minute: number
  available = true

  constructor(hour: number, minute: number) {
    this.hour = hour
    this.minute = minute
  }

  static fromString(timeStr: string): SimpleTime {
    const timeSplit = timeStr.split(":")
    const hour = +timeSplit[0]
    const minute = +timeSplit[1]
    return new SimpleTime(hour, minute)
  }

  toLocalTimeStr(): string {
    return this.toTimeString('24h')
  }

  toTimeString(type = '12h') {
    const suffix = this.hour >= 12 ? 'PM' : 'AM';
    const hour = type == '12h' ? (this.hour > 12 ? this.hour % 12 : this.hour) : this.hour
    const hourStr = (hour < 10 ? '0' : '') + `${hour}`;
    const minStr = (this.minute < 10 ? '0' : '') + `${this.minute}`
    return type == '12h' ? `${hourStr}:${minStr} ${suffix}` : `${hourStr}:${minStr}`
  }

  add(duration: number) {
    return this.numberToSimpleTime(this.toNumber() + duration)
  }

  toNumber() {
    return this.hour * 60 + this.minute;
  }

  numberToSimpleTime(number: number): SimpleTime {
    const hour = Math.floor(number / 60);
    const min = number % 60;
    return new SimpleTime(hour, min);
  }

}

export enum BusinessType {
  NAIL_SALON = 'NAIL_SALON',
  HAIR_SALON = 'HAIR_SALON',
  MESSAGE_SALON = 'MESSAGE_SALON',
  MED_SPA = 'MED_SPA',
  OTHER = 'OTHER',
}

export class Address {
  geoLocation: GeoLocation = new GeoLocation();
  addressLine1 = '';
  addressLine2 = '';
  city = '';
  state = '';
  country = '';
  zipcode = '';
}

export class GeoLocation {
  lat = 0;
  lng = 0;

  constructor(lat = 0, lng = 0) {
    this.lat = lat;
    this.lng = lng;
  }
}

export class ListResp<T> {
  data: T[] = [];
  /** the total across pages, where the endpoint sends one */
  total = 0;
}

export class Country {
  name = '';
  abbreviation = '';

  constructor(name = '', abbreviation = '') {
    this.name = name;
    this.abbreviation = abbreviation;
  }
}

export interface DomainInfoReq {
  domain: string;
  businessId: string;
}

export interface DomainInfo {
  domain: string;
  businessId: string;
}

export interface Theme {
  id: string;
}

export interface WebBuilderConfig {
  businessId: string;
  theme: Theme;
}

export interface WebBuilderConfigUpdateReq {
  themeId: string;
}


export const SFHeader = {
  STORE_ID: "salonfriday-store-id",
  BUSINESS_ID: "salonfriday-business-id"
}

export class CreatedId {
  id = '';
}

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum DayOfWeek {
  SUNDAY = "SUNDAY",
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY"
}


export interface FeedDataRequest {
  businessId: string;
  storeId: string;
  businessType: BusinessType
  enforce: boolean
}

export interface FeedDataResponse {
  message: string;
}

export interface BusinessNavigationItem {
  id: string;
  path: string;
  title: string;
  hidden: boolean;
  editable: boolean;
  show: boolean;
}

export interface TelnyxSetting {
  profileId: string;
  phoneNumber: string;
  bearerToken: string;
}

export interface TelnyxSettingRequest {
  storeId: string;
  telnyxSetting: TelnyxSetting;
}

